import { useEffect, useRef } from 'react';

/**
 * Тактильный отклик. iOS Safari не умеет navigator.vibrate, но Telegram WebApp
 * даёт HapticFeedback (работает на iPhone). Fallback — vibrate (Android).
 */
export function haptic(kind: 'tick' | 'limit' = 'tick') {
  try {
    const tg = (window as any).Telegram?.WebApp?.HapticFeedback;
    if (tg?.impactOccurred) {
      tg.impactOccurred(kind === 'limit' ? 'rigid' : 'light');
      return;
    }
  } catch { /* ignore */ }
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(kind === 'limit' ? 25 : 8);
  }
}

interface PinchOptions {
  /** Текущий размер (точка отсчёта для нового жеста). */
  value: number;
  min: number;
  max: number;
  /** Вызывается с новым размером во время щипка. Мемоизируй у вызывающего. */
  onChange?: (size: number) => void;
  /** Выключить (нет шаблона/onChange). */
  enabled?: boolean;
}

/**
 * Pinch-to-resize: двумя пальцами в превью меняем размер шрифта.
 * Старт каждого жеста — от текущего value (захват через ref), масштаб = текущая
 * дистанция / стартовая. Один палец не трогаем (скролл/перетаскивание позиции).
 */
export function usePinchFontSize(
  ref: React.RefObject<HTMLElement>,
  { value, min, max, onChange, enabled = true }: PinchOptions,
) {
  const start = useRef<{ dist: number; val: number } | null>(null);
  const valRef = useRef(value);
  valRef.current = value;
  const lastEmit = useRef(value);
  const pending = useRef<number | null>(null);
  const raf = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const dist = (t: TouchList) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        start.current = { dist: dist(e.touches), val: valRef.current };
        lastEmit.current = valRef.current;
      }
    };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && start.current) {
        e.preventDefault(); // блокируем зум страницы во время щипка
        const ratio = dist(e.touches) / (start.current.dist || 1);
        const next = Math.max(min, Math.min(max, Math.round(start.current.val * ratio)));
        if (next !== lastEmit.current) {
          // тик на шаг; сильнее на пределе (упёрлись в min/max)
          haptic(next === min || next === max ? 'limit' : 'tick');
          lastEmit.current = next;
          // Коалесим тяжёлый onChange (burn у поста) до 1 раза в кадр.
          pending.current = next;
          if (raf.current == null) {
            raf.current = requestAnimationFrame(() => {
              raf.current = null;
              if (pending.current != null) onChangeRef.current?.(pending.current);
            });
          }
        }
      }
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        start.current = null;
        // Гарантируем финальное значение после отрыва пальцев.
        if (raf.current != null) { cancelAnimationFrame(raf.current); raf.current = null; }
        if (pending.current != null) { onChangeRef.current?.(pending.current); pending.current = null; }
      }
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, [ref, min, max, enabled]);
}
